import {
  ChevronDownIcon,
  ChevronRightIcon,
  ReceiptTextIcon,
} from 'lucide-react';

import { useId, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { formatAmountForCurrency } from '@splitfiesta/shared';
import { Avatar, Card } from '@splitfiesta/ui';

import { SelectCircle } from '@/components/common/SelectCircle';

import { ScanNotReconciledWarning } from './ScanNotReconciledWarning';

export interface SplitByItemMember {
  userId: string;
  name: string;
  profileImage?: string;
  included: boolean;
}

export interface SplitByItemCardProps {
  /** The group members with the include state every split method shares. */
  members: SplitByItemMember[];
  onToggleParticipant: (userId: string) => void;
  itemCount: number;
  /** Units nobody holds across the list; shared by everyone. */
  unpickedUnits?: number;
  fromScan: boolean;
  /** A scan that did not add up, its list not edited since. */
  scanUnreconciled?: boolean;
  /** Entry-currency shares from the preview; empty while it cannot be worked out. */
  shares: Array<{ userId: string; amount: number }>;
  currency: string;
  onOpen: () => void;
  disabled?: boolean;
  /** The group cannot itemize (the example): who is in cannot change, the card still opens the list. */
  readOnly?: boolean;
}

export function SplitByItemCard({
  members,
  onToggleParticipant,
  itemCount,
  unpickedUnits = 0,
  fromScan,
  scanUnreconciled = false,
  shares,
  currency,
  onOpen,
  disabled = false,
  readOnly = false,
}: SplitByItemCardProps) {
  const { t } = useTranslation('expenses');
  const [listOpen, setListOpen] = useState(false);
  const listId = useId();
  const participants = members.filter(m => m.included);
  const nameOf = (userId: string) =>
    members.find(m => m.userId === userId)?.name ?? '';

  return (
    <Card className="space-y-4" data-testid="split-by-item-card">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {fromScan
          ? t('lineItems.hintScanned', { count: itemCount })
          : t('lineItems.hint')}
      </p>

      <div>
        <button
          type="button"
          aria-expanded={listOpen}
          aria-controls={listId}
          onClick={() => setListOpen(open => !open)}
          disabled={disabled}
          className="flex min-h-11 w-full flex-wrap items-center gap-2 rounded-lg text-left text-sm font-medium text-gray-900 hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-100 dark:hover:text-primary-400"
        >
          <span>{t('lineItems.whoIsIn')}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
            {participants.length}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className={`ml-auto h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none ${listOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {listOpen ? (
          <ul id={listId} className="mt-2 space-y-1">
            {members.map(member => {
              const lastOne = member.included && participants.length === 1;
              const locked = disabled || readOnly || lastOne;
              return (
                <li key={member.userId}>
                  <label
                    className={`relative flex min-h-11 items-center gap-3 rounded-lg px-2 py-1 ${
                      locked
                        ? 'cursor-default'
                        : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={member.included}
                      disabled={locked}
                      onChange={() => onToggleParticipant(member.userId)}
                      className="peer sr-only"
                    />
                    <SelectCircle
                      checked={member.included}
                      disabled={locked}
                      className="peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-900"
                    />
                    <span aria-hidden="true" className="shrink-0">
                      <Avatar
                        name={member.name}
                        src={member.profileImage}
                        size="xs"
                      />
                    </span>
                    <span className="min-w-0 flex-1 break-words text-base text-gray-900 dark:text-gray-100">
                      {member.name}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        className="flex w-full items-center gap-3 rounded-xl border-2 border-primary-200 bg-primary-50/60 p-4 text-left hover:border-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-primary-500/30 dark:bg-primary-500/10 dark:hover:border-primary-500/50"
      >
        <ReceiptTextIcon
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400"
        />
        <span className="min-w-0 flex-1">
          {itemCount === 0 ? (
            <>
              <span className="block text-base font-medium text-gray-900 dark:text-gray-100">
                {t('lineItems.noItems')}
              </span>
              <span className="block text-sm text-gray-600 dark:text-gray-400">
                {t('lineItems.addLines')}
              </span>
            </>
          ) : (
            <>
              <span className="block text-base font-medium text-gray-900 dark:text-gray-100">
                {t('lineItems.collapsed', { count: participants.length })}
              </span>
              {unpickedUnits > 0 ? (
                <span
                  data-testid="split-by-item-unpicked"
                  className="block text-sm text-gray-600 dark:text-gray-400"
                >
                  {t('lineItems.unitsUnpicked', { count: unpickedUnits })}
                </span>
              ) : null}
              {shares.length > 0 ? (
                <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm">
                  {shares.map(share => (
                    <span key={share.userId}>
                      <span className="text-gray-600 dark:text-gray-400">
                        {nameOf(share.userId)}
                      </span>{' '}
                      <span className="font-semibold text-gray-900 tabular-nums dark:text-gray-100">
                        {formatAmountForCurrency(share.amount, currency)}
                      </span>
                    </span>
                  ))}
                </span>
              ) : null}
            </>
          )}
        </span>
        <ChevronRightIcon
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500"
        />
      </button>
      {scanUnreconciled ? <ScanNotReconciledWarning /> : null}
    </Card>
  );
}

export default SplitByItemCard;
